// Disposition bar (SPEC §15.1, REVIEW-2026-10-01): 9 outcome buttons with hotkeys, callback picker with prospect-local
// presets, objection category, notes, Send email (2026-10-03: the old mailto 'Follow-up email' button is gone: it sent
// from the setter's own mail app, unlogged, with no unsubscribe check). Required before the next lead. Enter saves and dials the next lead
// (one keypress); Shift+Enter saves without dialing. The spoke-to-DM box and the AI "Tidy" are gone: the outcome says it.
// Text (2026-10-03): opens the Text panel for the lead on screen whenever there is one, during or after the call;
// disabled, with the server's sentence, when the lead can't be texted at all (GET /api/sms/business/:id `blocked`).
// Call back now (C, 2026-10-06): hung up on or cut off? Saves this call and redials the same number at once, from the same
// caller ID, within 15 minutes (pressing C is the setter's yes since 2026-10-20; never past 3 calls a day to one number);
// disabled with the server's sentence when the rules refuse it.
// The other line (A, PJ 2026-10-23): after a call to the owner's line, "Call the store now" saves this call (No answer with
// "Owner didn't answer; calling the store" when nothing was picked) and dials the store line at once; after a store-line
// call, "Call the owner now" the same way. Only after No answer, a voicemail, a gatekeeper or a wrong number.
import { useEffect, useMemo, useState } from 'preact/hooks';
import { OBJECTIONS, type Campaign, type DispositionCode, type ObjectionCategory } from '../../../src/shared/api';
import { useNow } from '../components/ui-extra';
import { buttonLabel, DISPOSITION_META, DISPOSITION_ORDER, OBJECTION_LABELS, outcomeButtonFor } from './labels';
import type { TemplateVars } from './script';
import {
  callBackButton, callBackNow, callBackTitle, callOtherLine, chooseDisposition, otherLineNow, saveAndDialNext, saveDisposition, updateWrap, wrapProblem,
  type DialerState,
} from './store';
import { fmtPhone } from './labels';
import { numberOf } from './numbers';
import { phone } from '../phone/manager';
import { noCampaignProblem } from './wrap';
import { callbackPresets, fmtDayTime, fromDatetimeLocal, outsideDefaultHours, toDatetimeLocal } from './time';
import { knownSmsBlock, loadSmsBlock, onSmsBlock } from './sms';

/** Why this lead can't be texted at all (the server's sentence), or null while it can (or isn't known yet). */
function useSmsBlocked(businessId: number | null): string | null {
  const [blocked, setBlocked] = useState<string | null>(null);
  useEffect(() => {
    if (!businessId || businessId <= 0) { setBlocked(null); return; }
    setBlocked(knownSmsBlock(businessId)?.blocked ?? null);
    const off = onSmsBlock((id, b) => { if (id === businessId) setBlocked(b); });
    loadSmsBlock(businessId);
    return off;
  }, [businessId]);
  return blocked;
}

export function WrapUpDock(props: { s: DialerState; campaign: Campaign | null; vars: TemplateVars; onBook: () => void; onEmail: () => void; onText: () => void }) {
  const { s } = props;
  const item = s.item;
  const w = s.wrap;
  const phase = s.call.phase;
  const live = phase === 'connecting' || phase === 'ringing' || phase === 'in_call';
  const owed = phase === 'needs_disposition';
  const canPick = !!item && (live || owed);
  const now = useNow(30000);
  const tz = item?.business.tz ?? 'America/Toronto';
  // PJ 2026-10-21: a callback can be any time from now on, so "In 30 min" and "In 1 hour" come first. After a refused
  // save (400 callback_not_allowed: calling hours, or a 4th call to the number in 24 hours) the server's earliest allowed
  // time takes the "later today" place. The lead's gate retryAt is no longer used here: it waits out the soft rules (4 h
  // spacing...), which no longer limit a callback.
  const earliestAt = w.callbackEarliestAt;
  const presets = useMemo(() => { try { return callbackPresets(new Date(now), tz, { earliestAt }); } catch { return []; } }, [now, tz, earliestAt]);
  const needsCallback = !!w.code && DISPOSITION_META[w.code].needsCallback;
  const cbDate = w.callbackAt ? new Date(w.callbackAt) : null;
  const cbWarn = cbDate ? (() => { try { return outsideDefaultHours(cbDate, tz); } catch { return null; } })() : null;
  const problem = owed ? wrapProblem(s) : null;
  // No campaign (SPEC §19, 2026-10-02): the wrap-up ends with Save to My leads / Don't save instead of save-and-dial.
  const noCampaign = !!item?.noCampaign;
  const saveWhy = owed && noCampaign ? problem ?? noCampaignProblem(w, true) : null;
  const dropWhy = owed && noCampaign ? problem ?? noCampaignProblem(w, false) : null;


  const pick = (code: DispositionCode) => { chooseDisposition(code); };
  // Post-call email (SPEC §19, 2026-10-02): any call that reached Twilio (the server confirms it was answered).
  // No email after a voicemail: our emails say we spoke on the phone (the server refuses it too).
  const leftVoicemail = w.code === 'vm_live' || s.call.serverDisposition === 'vm_dropped';
  const emailable = s.call.callId !== null && (phase === 'in_call' || owed) && !leftVoicemail;
  // Texting is about the lead, not the call: any lead with a business, before, during or after the call, unless the
  // server says it can't be texted (do-not-contact, dispensary, a closed state / Quebec / Canadian healthcare / National
  // DNC when they never texted us).
  const smsBlocked = useSmsBlocked(item && item.business.id > 0 ? item.business.id : null);
  const textable = !!item && item.business.id > 0 && !smsBlocked;
  const lit = outcomeButtonFor(w.code);
  // "Call back now": shown in the wrap-up of a call that reached their line (store.callBackButton says why it's off).
  const cb = owed ? callBackButton(s, now) : null;
  const cbBlock = cb ? cb.why ?? phone.whyCantCall() : null;
  const callBack = cb ? (
    <button class="btn" disabled={!!cbBlock} title={cbBlock ?? callBackTitle(s)}
      onClick={(e) => { (e.currentTarget as HTMLElement).blur(); void callBackNow(); }}>
      Call back now <kbd>C</kbd>
    </button>
  ) : null;
  // PJ 2026-10-23: "Call the store now" / "Call the owner now" (A) after a call to one of the lead's two numbers.
  const other = owed ? otherLineNow(s) : null;
  const otherBlock = other ? other.why ?? phone.whyCantCall() : null;
  const otherNumber = other && item ? numberOf(item, other.target) : null;
  const otherLine = other ? (
    <button class="btn" disabled={!!otherBlock} title={otherBlock ?? `Logs this call (No answer if you picked nothing) and dials ${otherNumber ? fmtPhone(otherNumber.e164) : 'the other number'} right away`}
      onClick={(e) => { (e.currentTarget as HTMLElement).blur(); void callOtherLine(); }}>
      {other.label} <kbd>A</kbd>
    </button>
  ) : null;
  const cbHint = cb && !cbBlock ? ' C calls them back now.' : '';
  const cbNone = cb && !cbBlock ? ' Hung up on or cut off? C logs it as No answer and calls them back now.' : ''; // nothing picked yet

  return (
    <div class={`vd-dock ${owed ? 'vd-dock-owed' : ''}`}>
      <div class="vd-dock-codes" role="group" aria-label="Call outcome">
        {DISPOSITION_ORDER.map((code) => {
          const meta = DISPOSITION_META[code];
          return (
            <button class={`vd-code vd-code-${meta.tone} ${lit === code ? 'on' : ''}`} disabled={!canPick || s.call.saving}
              aria-pressed={lit === code} title={meta.hint}
              onClick={(e) => { pick(code); (e.currentTarget as HTMLElement).blur(); }}>
              <kbd>{meta.key}</kbd><span>{buttonLabel(code)}</span>
            </button>
          );
        })}
      </div>

      <div class="vd-dock-form">
        <div class="row" style="gap:10px;flex-wrap:wrap">
          <select aria-label="Objection" disabled={!canPick} value={w.objection} onChange={(e) => {
            const v = e.currentTarget.value as ObjectionCategory | '';
            updateWrap({ objection: v });
            if (v === 'send_info' && emailable) props.onEmail();   // "Send me info": open the email panel
          }}>
            <option value="">Objection: none</option>
            {OBJECTIONS.map((o) => <option value={o}>{OBJECTION_LABELS[o]}</option>)}
          </select>
          <button class="btn btn-sm btn-primary" disabled={!emailable} title={emailable ? 'Draft a follow-up email from this call; you review it before it sends' : leftVoicemail ? 'No email after a voicemail: our emails say we spoke on the phone' : 'Available on a connected call'}
            onClick={(e) => { (e.currentTarget as HTMLElement).blur(); props.onEmail(); }}>Send email</button>
          <button class="btn btn-sm" disabled={!textable} title={textable ? 'Text this lead from our number; replies show here and under Texts' : smsBlocked ?? 'Open a lead first'}
            onClick={(e) => { (e.currentTarget as HTMLElement).blur(); props.onText(); }}>Text <kbd>T</kbd></button>
          {smsBlocked ? <span class="muted" style="font-size:12px">{smsBlocked}</span> : null}
        </div>

        {needsCallback ? (
          <div class="vd-callback">
            <span class="eyebrow">Call back (their time)</span>
            <div class="row" style="gap:6px;flex-wrap:wrap">
              {presets.map((p) => (
                <button key={p.key} class={`btn btn-sm ${w.callbackAt === p.at.toISOString() ? 'btn-ink' : p.key === 'earliest' && w.callbackEarliestAt ? 'btn-primary' : ''}`}
                  title={p.key === 'earliest' ? 'The earliest time the calling rules allow a call back to this number' : undefined}
                  onClick={() => updateWrap({ callbackAt: p.at.toISOString() })}>{p.label}</button>
              ))}
              <input type="datetime-local" aria-label="Callback time, their time zone"
                value={cbDate ? (() => { try { return toDatetimeLocal(cbDate, tz); } catch { return ''; } })() : ''}
                onInput={(e) => { const d = fromDatetimeLocal(e.currentTarget.value, tz); updateWrap({ callbackAt: d ? d.toISOString() : null }); }} />
            </div>
            {cbDate ? (
              <span class="muted" style="font-size:13px">
                {fmtDayTime(cbDate, tz)} their time · {cbDate.toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' })} yours
                {cbWarn ? <span class="vd-bad"> · {cbWarn}</span> : null}
              </span>
            ) : null}
          </div>
        ) : null}

        <div class="vd-notes">
          <textarea rows={2} disabled={!item} value={w.note} placeholder="Notes: who you spoke to, what they said, next step. Ctrl+Enter saves and dials the next lead; Ctrl+Shift+Enter saves only."
            onInput={(e) => updateWrap({ note: e.currentTarget.value })}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' || !(e.ctrlKey || e.metaKey) || e.repeat) return;
              e.preventDefault();
              void (e.shiftKey ? saveDisposition() : saveAndDialNext()); // no campaign: asks for Save to My leads / Don't save
            }} />
        </div>

        {noCampaign ? (
          <div class="row" style="gap:10px;flex-wrap:wrap">
            <button class="btn btn-primary" disabled={!owed || s.call.saving || !!saveWhy} title={saveWhy ?? 'Keeps this number in My leads with this outcome and your notes'}
              onClick={() => void saveDisposition({ saveLead: true })}>
              {s.call.saving ? 'Saving…' : 'Save to My leads'}
            </button>
            <button class="btn" disabled={!owed || s.call.saving || !!dropWhy} title={dropWhy ?? 'Hides this number from My leads and every list. The call, its outcome and any do-not-call request are kept.'}
              onClick={() => void saveDisposition({ saveLead: false })}>
              Don't save
            </button>
            {callBack}
            {otherLine}
            <span class={owed ? 'vd-owed-msg' : 'muted'} style="font-size:13px;flex:1;min-width:200px">
              {s.call.saving ? 'Saving…'
                : owed ? (s.call.saveError ?? (problem ? `${problem}${w.code ? '' : cbNone}` : null) ?? (w.code
                  ? `Outcome: ${buttonLabel(outcomeButtonFor(w.code) ?? w.code)}${w.code === 'dnc_request' ? ' (adds them to do-not-call, saved or not)' : ''}. No campaign: Save to My leads keeps it; Don't save hides it from every list (the call, outcome and any do-not-call request are kept).${cbHint}`
                  : 'Pick the outcome, then Save to My leads or Don\'t save.'))
                  : live ? 'Pick the outcome now or when the call ends. No campaign: afterwards, save it to My leads or not.'
                    : phase === 'ended' ? 'Call ended. Checking whether it needs an outcome…'
                      : 'No campaign. Outcome buttons unlock once you dial.'}
            </span>
          </div>
        ) : (
        <div class="row" style="gap:10px;flex-wrap:wrap">
          <button class="btn btn-primary" disabled={!owed || s.call.saving || !!problem} onClick={() => void saveAndDialNext()}
            title="Saves the outcome, loads the next lead and dials it. Your click is the dial: nothing dials on its own.">
            {s.call.saving ? 'Saving…' : <>Save &amp; dial next <kbd>Enter</kbd></>}
          </button>
          <button class="btn" disabled={!owed || s.call.saving || !!problem} onClick={() => void saveDisposition()}
            title="Saves the outcome and loads the next lead without dialing it">
            Save only <kbd>Shift+Enter</kbd>
          </button>
          {callBack}
          {otherLine}
          <span class={owed ? 'vd-owed-msg' : 'muted'} style="font-size:13px;flex:1;min-width:200px">
            {s.call.saving ? 'Saving…'
              : owed ? (s.call.saveError ?? (s.call.dropping ? 'Still confirming the voicemail drop; if it went through, this closes on its own. ' : '')
                + ((problem ? `${problem}${w.code ? '' : cbNone}` : null) ?? (w.code ? `Outcome: ${buttonLabel(outcomeButtonFor(w.code) ?? w.code)}${w.code === 'dnc_request' ? ' (adds them to do-not-call on every campaign)' : w.code === 'dm_not_interested' ? ' (the business rests 90 days, every campaign)' : ''}. Enter saves and dials the next lead; Shift+Enter saves only.${cbHint}` : 'Pick the outcome.')))
                : live ? 'Pick the outcome now or when the call ends; it saves after the call.'
                  : phase === 'ended' ? (s.call.dropping ? 'Call ended. Confirming the voicemail drop…' : 'Call ended. Checking whether it needs an outcome…')
                    : 'Outcome buttons unlock once you dial.'}
          </span>
        </div>
        )}
      </div>
    </div>
  );
}
